import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { blurRadii, check, hasLongTravel } from '../skillUsage';

export const whipPanCutSkill: CodeoSkill = {
    name: SKILL_NAMES.whipPanCut,
    kind: 'codeo',
    description:
        'Throw the camera sideways fast enough to smear, and land on the next state already ' +
        'moving — the whip pan, for an energetic change of subject that keeps its momentum.',
    documentUsesSkill: check(
        (script) => blurRadii(script).some((radius) => radius >= 15) && hasLongTravel(script),
    ),
    instructions: `# Whip Pan Cut

The frame accelerates sideways until it is a blur, and decelerates into a different state. The cut is hidden inside the fastest part of the move, so the two states never appear together and the viewer reads one continuous camera whip rather than a transition.

It is the loud, energetic sibling of \`${SKILL_NAMES.velocityMatchedCut}\`: the same principle of hiding a cut inside motion, with the motion turned up until it smears.

## Markup

Both states are full-frame, side by side inside a stage wider than the viewport.

\`\`\`html
<div id="whip-viewport" style="position: absolute; inset: 0; overflow: hidden;">
  <div id="whip-stage" data-l5-allow-offscreen style="position: absolute; left: 0; top: 0; width: 3840px; height: 1080px;">
    <div style="position: absolute; left: 0; top: 0; width: 1920px; height: 1080px; display: flex; align-items: center; justify-content: center; background: var(--l5-color-1, #101820); font-family: var(--l5-font-0), sans-serif; font-size: 132px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Four tools</div>
    <div style="position: absolute; left: 1920px; top: 0; width: 1920px; height: 1080px; display: flex; align-items: center; justify-content: center; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 132px; font-weight: 800; color: var(--l5-color-1, #101820);">One</div>
  </div>
</div>
\`\`\`

## Timeline code

The blur ramps up and back down inside the move, peaking exactly where the travel is fastest. That peak is where the cut hides.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const WHIP = 1.6;
const SPAN = 0.44;

gsap.set('#whip-stage', { x: 0, filter: 'blur(0px)' });

tl.to('#whip-stage', { x: -1920, duration: SPAN, ease: 'power3.inOut' }, WHIP);
tl.to('#whip-stage', { filter: 'blur(26px)', duration: SPAN / 2, ease: 'power2.in' }, WHIP);
tl.to('#whip-stage', { filter: 'blur(0px)', duration: SPAN / 2, ease: 'power2.out' }, WHIP + SPAN / 2);

window.__timelines = { main: tl };
\`\`\`

\`power3.inOut\` puts the maximum velocity in the middle of the move, which is exactly where the blur peaks — the two have to agree or the smear looks bolted on.

## Adapt

- **Speed**: 0.35–0.5s for the whole pan. Slower and it is a slide with a blur on it; faster and the viewer cannot tell what happened.
- **Blur depth**: 20–34px. The blur should be strong enough that neither state is readable at the midpoint — that unreadability is what hides the cut.
- **Vertical**: the same move on \`y\` reads as a flick up or down, useful for a change of altitude rather than of subject.
- **Directional streak**: a set of thin translucent bars travelling with the pan adds speed lines. Keep them subtle or it becomes a cartoon.
- **Land moving**: letting the arriving state's own content settle for 0.3s after the pan stops keeps the momentum alive rather than stopping dead.
- **Chain two**: whipping twice through three states works, but the middle state needs a real hold or it is never seen.

## Pitfalls

- The stage is \`x\`-tweened and reaches far outside the frame, so it must not carry a CSS \`transform\` and it needs \`data-l5-allow-offscreen\`.
- Tween the whole \`filter\` string with units in both endpoints; a bare number will not interpolate.
- The blur peak must coincide with the velocity peak. Offsetting them makes the smear read as a separate effect rather than as motion.
- Full-frame blur at 26px is expensive to rasterize. It only runs for a fraction of a second here, which is what makes it affordable — don't extend it.
- Never whip between two states the viewer needs to compare. Whatever was on screen before is gone and unrecoverable.
- One whip per video. It is a strong stylistic statement and the second one costs the first its energy.
- Both states must be full-frame and exactly viewport-width apart, or the pan lands off-centre.`,
};
